import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import PatientLabUpload from "@/components/doctor/PatientLabUpload";
import PatientDILPanel from "@/components/doctor/PatientDILPanel";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  Activity, Scale, Ruler, TrendingUp, TrendingDown, Sparkles, Loader2,
  FileText, Plus, Send, CalendarClock, Dna, Utensils, Eye,
} from "lucide-react";

const PhenotypeClassifier = lazy(() => import("@/components/PhenotypeClassifier"));
const MealIntelligence    = lazy(() => import("@/components/MealIntelligence"));
const DoctorAIAssistant   = lazy(() => import("@/components/doctor/DoctorAIAssistant"));
const PhenotypeSynergyPanel = lazy(() => import("@/components/PhenotypeSynergyPanel"));
const DoctorMealIntelligenceSummary = lazy(() => import("@/components/doctor/DoctorMealIntelligenceSummary"));
const DoctorPatientLongevity = lazy(() => import("@/components/doctor/DoctorPatientLongevity"));
const PhenotypeRecommendationsPanel = lazy(() => import("@/components/doctor/PhenotypeRecommendationsPanel"));
const HematologyAIPanel = lazy(() => import("@/components/HematologyAIPanel"));
const LabUnitConverter = lazy(() => import("@/components/doctor/LabUnitConverter"));
const PatientRiskHeatmap = lazy(() => import("@/components/doctor/PatientRiskHeatmap"));
const Col3ReadinessWidget = lazy(() => import("@/components/doctor/Col3ReadinessWidget"));
// iter226e removed from Doctor Portal per user request
// (a duplicate Gail/BCRAT lives in the patient
// BreastHealthPage / BreastCancerRiskPanel; that one stays).

function getToken() {
  return localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
}

/** Patient Detail View — Overview tab only for Phase A MVP. */
export default function PatientDetailView(props) {
  return (
    <ErrorBoundary label="Patient Detail">
      <PatientDetailViewInner {...props} />
    </ErrorBoundary>
  );
}

function PatientDetailViewInner({ patient, onRefresh }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [newNote, setNewNote] = useState("");

  const fetchDetail = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.get(`${API}/doctor/patients/${patient.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [API, patient?.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleAnalyze = async () => {
    setAnalysisLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/doctor/patients/${patient.id}/analyze`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(bg ? "AI анализът е готов" : "AI analysis ready");
      await fetchDetail();
    } catch {
      toast.error(bg ? "Грешка при анализа" : "Analysis error");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const token = getToken();
      await axios.post(`${API}/doctor/patients/${patient.id}/notes`, { note: newNote }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setNewNote("");
      await fetchDetail();
      toast.success(bg ? "Бележката е добавена" : "Note added");
    } catch {
      toast.error(bg ? "Грешка" : "Error");
    }
  };

  const handleInvite = async () => {
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients/${patient.id}/invite`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      navigator.clipboard?.writeText(data.activation_link);
      toast.success(bg ? "Линкът е копиран в клипборда" : "Link copied to clipboard");
      if (data.email_sent) toast.info(bg ? "Имейл е изпратен на пациента" : "Email sent to patient");
    } catch {
      toast.error(bg ? "Грешка при поканата" : "Invitation error");
    }
  };

  if (loading || !detail) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  const p = detail;
  const profile = p.profile || {};
  const labs = profile.labValues || {};
  const analysis = p.latest_analysis;

  const cardCls = `rounded-2xl border p-4 sm:p-5
    ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  const SEV_COLOR = {
    low:      "text-emerald-500", good: "text-emerald-500",
    moderate: "text-amber-500",
    high:     "text-rose-500",
  };

  const tabs = [
    { key: "overview",  label_bg: "Преглед",    label_en: "Overview" },
    { key: "data",      label_bg: "Данни",      label_en: "Data" },
    { key: "dil",       label_bg: "DIL",        label_en: "DIL" },
    { key: "phenotype", label_bg: "Фенотип",    label_en: "Phenotype" },
    // #8 — „Попитай AI" преди „Бележки"
    { key: "ask_ai",    label_bg: "Попитай AI", label_en: "Ask AI" },
    { key: "notes",     label_bg: "Бележки",    label_en: "Notes" },
  ];

  const initials = (p.name || "?").slice(0, 2).toUpperCase();

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="patient-detail-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-bold
            bg-gradient-to-br from-violet-500 to-fuchsia-600">
            {initials}
          </div>
          <div>
            <h2 className="text-xl font-bold" data-testid="patient-name">{p.name}</h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {p.age ? `${p.age} ${bg ? "г." : "y"}` : "—"}
              {p.gender && ` · ${p.gender === "male" ? (bg ? "мъж" : "Male") : p.gender === "female" ? (bg ? "жена" : "Female") : p.gender}`}
              {p.created_by_doctor && ` · ${bg ? "Създаден от вас" : "Created by you"}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`${API}/doctor/patients/${patient.id}/report.pdf?token=${encodeURIComponent(getToken() || '')}`}
            onClick={async (e) => {
              e.preventDefault();
              try {
                const token = getToken();
                const res = await axios.get(`${API}/doctor/patients/${patient.id}/report.pdf`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                  responseType: "blob",
                });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url; a.download = `nutramilo-patient-${String(patient.id).slice(0,8)}.pdf`;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
              } catch {
                toast.error(bg ? "Грешка при генериране на PDF" : "PDF generation failed");
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-pink-500 to-violet-600 text-white text-xs font-semibold hover:opacity-95"
            data-testid="doctor-pdf-report-btn"
          >
            <FileText className="w-3.5 h-3.5" />
            {bg ? "PDF доклад" : "PDF report"}
          </a>
          {!p.is_activated && (
            <button
              onClick={handleInvite}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600
                text-white text-xs font-semibold hover:opacity-95"
              data-testid="invite-patient-btn"
            >
              <Send className="w-3.5 h-3.5" />
              {bg ? "Покани" : "Invite"}
            </button>
          )}
          {/* #9 — Връзка към пациентския профил (същата flow като register) */}
          {p.is_activated && (
            <a
              href={`/dashboard?as_patient=${patient.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold hover:opacity-95"
              data-testid="view-as-patient-btn"
            >
              <Eye className="w-3.5 h-3.5" />
              {bg ? "Виж профила" : "View profile"}
            </a>
          )}
          <button
            onClick={handleAnalyze}
            disabled={analysisLoading}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600
              text-white text-xs font-semibold hover:opacity-95 disabled:opacity-60"
            data-testid="generate-analysis-btn"
          >
            {analysisLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {bg ? "AI анализ" : "AI Analyze"}
          </button>
        </div>
      </div>

      {/* Trial banner */}
      {p.trial_started_by_doctor && p.trial_days_left != null && (
        <div className={`rounded-xl p-3 border flex items-center gap-2 text-sm
          ${isDark ? "bg-emerald-500/10 border-emerald-900/40 text-emerald-300"
                   : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
          data-testid="trial-banner">
          <CalendarClock className="w-4 h-4" />
          <span>
            {bg ? `Пациентът има пълен достъп · остават ${p.trial_days_left} дни`
                : `Patient has full access · ${p.trial_days_left} days remaining`}
          </span>
        </div>
      )}

      {/* Sub tabs — iter398: horizontal scroll on mobile + larger tap targets,
          Notes tab pinned right and styled as drawer trigger. Whitespace-nowrap
          prevents label truncation and -mx-4/-mx-6 lets the row bleed to the
          phone edges for full-width scroll. */}
      <div className="flex items-stretch gap-1 border-b -mx-4 sm:-mx-6 px-4 sm:px-6
        overflow-x-auto scrollbar-thin border-gray-200 dark:border-white/5"
        data-testid="patient-detail-subtabs">
        {tabs.map(t => {
          const isNotes = t.key === "notes";
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-shrink-0 whitespace-nowrap px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-colors
                ${tab === t.key
                  ? `border-violet-500 ${isNotes ? "text-amber-500" : "text-violet-500"}`
                  : `border-transparent ${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-800"}`}
                ${isNotes ? "ml-auto" : ""}`}
              data-testid={`detail-tab-${t.key}`}
            >
              <span className="inline-flex items-center gap-1">
                {isNotes && <span aria-hidden="true">📝</span>}
                {bg ? t.label_bg : t.label_en}
              </span>
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="overview-content">
          {/* Key metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              icon={Activity} label_bg="DIL Score" label_en="DIL Score"
              value={analysis?.insulin_resistance || "—"} color="rose" isDark={isDark} bg={bg}
            />
            <MetricCard
              icon={Scale} label_bg="Тегло" label_en="Weight"
              value={profile.body?.weight ? `${profile.body.weight} kg` : "—"} color="emerald" isDark={isDark} bg={bg}
            />
            <MetricCard
              icon={Ruler} label_bg="Височина" label_en="Height"
              value={profile.body?.height ? `${profile.body.height} cm` : "—"} color="sky" isDark={isDark} bg={bg}
            />
            <MetricCard
              icon={TrendingUp} label_bg="Статус" label_en="Status"
              value={p.status || "—"} color="violet" isDark={isDark} bg={bg}
            />
          </div>

          {/* DIL Trend sparkline */}
          {p.trend_30d && p.trend_30d.length > 0 && (
            <div className={cardCls} data-testid="dil-trend">
              <h3 className="text-sm font-semibold mb-2">{bg ? "Посока на DIL (30 дни)" : "DIL Trend (30 days)"}</h3>
              <MiniLineChart data={p.trend_30d} field="dil_avg" color="#f43f5e" />
            </div>
          )}

          {/* AI Analysis Summary */}
          {analysis ? (
            <div className={cardCls} data-testid="ai-analysis-summary">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">{bg ? "AI анализ (обобщение)" : "AI Analysis Summary"}</h3>
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                  {bg ? "Генерирано днес" : "Generated today"}
                </span>
              </div>
              <p className={`text-sm leading-relaxed mb-3 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                {bg ? analysis.summary_bg : analysis.summary_en}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["insulin_resistance", bg ? "Инсулинова резистентност" : "Insulin resistance"],
                  ["inflammation", bg ? "Възпаление" : "Inflammation"],
                  ["oxidative_stress", bg ? "Оксидативен стрес" : "Oxidative stress"],
                  ["lipid_balance", bg ? "Липиден баланс" : "Lipid balance"],
                ].map(([k, lbl]) => {
                  const v = analysis[k];
                  return (
                    <div key={k} className="flex items-center justify-between">
                      <span className={isDark ? "text-gray-400" : "text-gray-600"}>{lbl}</span>
                      <span className={`font-semibold capitalize ${SEV_COLOR[v] || "text-gray-500"}`}>{v || "—"}</span>
                    </div>
                  );
                })}
              </div>

              {analysis.top_recommendations?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/5">
                  <h4 className="text-sm font-semibold mb-2">{bg ? "Топ препоръки" : "Top Recommendations"}</h4>
                  <ul className="space-y-1.5 text-xs">
                    {analysis.top_recommendations.map((r, i) => {
                      const txt = typeof r === "string" ? r : bg ? r.bg : r.en;
                      return (
                        <li key={`rec-${txt}-${i}`} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                          <span>{txt}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className={`${cardCls} text-center py-8`}>
              <Sparkles className={`w-8 h-8 mx-auto mb-2 ${isDark ? "text-violet-400" : "text-violet-500"}`} />
              <p className="text-sm mb-3">
                {bg ? "Все още няма AI анализ за този пациент." : "No AI analysis yet for this patient."}
              </p>
              <button
                onClick={handleAnalyze}
                disabled={analysisLoading}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg
                  bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold hover:opacity-95 disabled:opacity-60"
              >
                {analysisLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {bg ? "Генерирай анализ" : "Generate analysis"}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "data" && (
        <div className="space-y-3" data-testid="data-content">
          <PatientEditForm
            patient={p}
            patientId={patient.id}
            API={API}
            isDark={isDark}
            bg={bg}
            cardCls={cardCls}
            onSaved={fetchDetail}
          />

          <PatientLabUpload patientId={patient.id} onUploaded={fetchDetail} />

          {/* iter397 — LabUnitConverter премахнат по заявка на потребителя. */}

          <PatientLabAnalysisList
            patientId={patient.id}
            API={API}
            isDark={isDark}
            bg={bg}
            cardCls={cardCls}
            onAnalyzed={fetchDetail}
          />

          {/* Hematology AI Assistant — временно скрит по заявка на потребителя (iter221). */}
          {/* <Suspense fallback={null}>
            <HematologyAIPanel patientId={patient.id} />
          </Suspense> */}

          <div className={cardCls}>
            <h3 className="text-sm font-bold mb-3">{bg ? "Лабораторни стойности (парснати)" : "Parsed lab values"}</h3>
            {Object.keys(labs).length === 0 ? (
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "Няма парснати лабораторни данни. Качете PDF и натиснете 'Анализирай' или въведете ръчно при създаване."
                    : "No parsed lab values. Upload a PDF and press 'Analyze' or enter manually during creation."}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {Object.entries(labs).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 border-b border-gray-100 dark:border-white/5">
                    <span className={isDark ? "text-gray-400" : "text-gray-600"}>{k}</span>
                    <span className="font-semibold">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "dil" && (
        <div className="space-y-3" data-testid="dil-content">
          <PatientDILPanel patientId={patient.id} patient={p} />
        </div>
      )}

      {tab === "phenotype" && (
        <div className="space-y-3" data-testid="phenotype-content">
          <div className={`rounded-xl p-2.5 text-xs flex items-start gap-2
            ${isDark ? "bg-violet-500/10 text-violet-300 border border-violet-900/40"
                     : "bg-violet-50 text-violet-700 border border-violet-200"}`}>
            <Dna className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              {bg ? "Клиничен фенотип базиран на данните на пациента." : "Clinical phenotype based on patient data."}
            </span>
          </div>
          {/* Meal Intelligence summary — винаги видима, замества празното 
              състояние „пациентът няма фенотипи" с действителни препоръки. */}
          <Suspense fallback={null}>
            <DoctorMealIntelligenceSummary patientId={patient.id} />
          </Suspense>
          {/* iter327e — Patient Risk Heatmap — top 3 critical drug/food/supplement
              interactions + age-related risks от iter327 ADR catalog. */}
          <Suspense fallback={null}>
            <PatientRiskHeatmap patientId={patient.id} />
          </Suspense>
          {/* iter327M — Col3 Synthesis Readiness Score (Stewart 2024) */}
          <Suspense fallback={null}>
            <Col3ReadinessWidget
              patientId={patient.id}
              apiBase={`${process.env.REACT_APP_BACKEND_URL || ""}/api`}
              token={getToken()}
              isDark={false}
            />
          </Suspense>
          {/* Longevity Score (DunedinPACE-style) — read-only снимка
              от последното изчисление на пациента. */}
          <Suspense fallback={null}>
            <DoctorPatientLongevity patientId={patient.id} />
          </Suspense>
          <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
            <PhenotypeClassifier />
          </Suspense>
          {/* Synergy detector for multi-phenotype patients */}
          <Suspense fallback={null}>
            <PhenotypeSynergyPanel patientId={patient.id} />
          </Suspense>
          {/* Phenotype-driven clinical recommendations (foods + nutrients +
              meds покриващи driver pathways на assigned phenotypes) */}
          <Suspense fallback={null}>
            <PhenotypeRecommendationsPanel patientId={patient.id} />
          </Suspense>
        </div>
      )}

      {tab === "meal_ai" && (
        <div className="space-y-3" data-testid="meal-ai-content">
          {/* Placeholder — Meal AI премахнато от docto portal по user request (Apr 26 2026) */}
        </div>
      )}

      {/* #8 — „Попитай AI" tab за doctor mode (преди Notes) */}
      {tab === "ask_ai" && (
        <div className="space-y-3" data-testid="ask-ai-content">
          <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
            <DoctorAIAssistant patientId={patient.id} patient={p} />
          </Suspense>
        </div>
      )}

      {tab === "notes" && (
        <div className="space-y-3" data-testid="notes-content">
          <div className={cardCls}>
            <h3 className="text-sm font-bold mb-2 flex items-center gap-1.5">
              <FileText className="w-4 h-4" />
              {bg ? "Нова бележка" : "New note"}
            </h3>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              placeholder={bg ? "Запишете клинични наблюдения или препоръки..." : "Record clinical observations or recommendations..."}
              className={`w-full p-3 text-sm rounded-lg border outline-none
                ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white placeholder:text-gray-500"
                         : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
              data-testid="new-note-input"
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              className="mt-2 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg
                bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold
                hover:opacity-95 disabled:opacity-50"
              data-testid="add-note-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              {bg ? "Добави бележка" : "Add note"}
            </button>
          </div>

          {p.notes?.length > 0 ? (
            p.notes.map((n, i) => (
              <div key={n._id || n.created_at || `note-${i}`} className={cardCls} data-testid={`note-${i}`}>
                <p className={`text-sm whitespace-pre-wrap ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {n.note}
                </p>
                <p className={`text-[10px] mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {n.doctor_name || "Doctor"} · {new Date(n.created_at).toLocaleString(bg ? "bg-BG" : "en-US")}
                </p>
              </div>
            ))
          ) : (
            <p className={`text-sm text-center py-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "Няма бележки" : "No notes yet"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label_bg, label_en, value, color, isDark, bg }) {
  const colorMap = {
    rose:    "from-rose-500 to-rose-600",
    emerald: "from-emerald-500 to-emerald-600",
    sky:     "from-sky-500 to-sky-600",
    violet:  "from-violet-500 to-violet-600",
  };
  return (
    <div className={`rounded-xl p-3 border
      ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white bg-gradient-to-br ${colorMap[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? label_bg : label_en}
        </div>
      </div>
      <div className={`text-lg font-bold capitalize ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{value}</div>
    </div>
  );
}

function MiniLineChart({ data, field, color = "#8b5cf6" }) {
  const points = data.filter(d => d[field] != null).map((d, i) => ({ x: i, y: d[field] }));
  if (points.length < 2) {
    return <div className="h-20 flex items-center justify-center text-xs text-gray-500">Not enough data</div>;
  }
  const W = 600, H = 80, P = 4;
  const xs = points.map(p => p.x), ys = points.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs) || 1;
  const minY = Math.min(...ys), maxY = Math.max(...ys) || 1;
  const nx = (x) => P + ((x - minX) / (maxX - minX || 1)) * (W - 2 * P);
  const ny = (y) => H - P - ((y - minY) / (maxY - minY || 1)) * (H - 2 * P);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${nx(p.x).toFixed(1)} ${ny(p.y).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" preserveAspectRatio="none">
      <path d={d} fill="none" stroke={color} strokeWidth="2" />
      <path d={`${d} L ${nx(maxX)} ${H} L ${nx(minX)} ${H} Z`} fill={color} opacity="0.1" />
    </svg>
  );
}


/**
 * PatientEditForm — пълен редактор на пациентско досие за doctor/admin.
 * (#6 — разширен едитор: identity, body, measurements, labs, lifestyle, goals)
 *
 * Записва чрез:
 *  - POST /doctor/patients/{id}/quick-input  (тегло/височина/симптоми/diagnosis)
 *  - PATCH /doctor/patients/{id}/profile     (име/email/age/gender)
 *  - PUT  /doctor/patients/{id}/profile-extras (measurements + lifestyle + goals + labs)
 */
function PatientEditForm({ patient, patientId, API, isDark, bg, cardCls, onSaved }) {
  const profile = patient?.profile || {};
  const personal = profile.personal || {};
  const body = profile.body || {};
  const health = profile.health || {};
  const measurements = profile.measurements || {};
  const lifestyle = profile.lifestyle || {};
  const goals = profile.goals || {};
  const labs = profile.labValues || {};
  const symptomsInit = Array.isArray(health.symptoms) ? health.symptoms.join(", ") : "";

  // Identity
  const [name, setName] = useState(patient?.name ?? "");
  const [email, setEmail] = useState(patient?.email ?? "");
  const [age, setAge] = useState(personal.age ?? "");
  const [gender, setGender] = useState(personal.gender ?? "");

  // Body
  const [weight, setWeight] = useState(body.weight ?? "");
  const [height, setHeight] = useState(body.height ?? "");

  // Measurements
  const [waist, setWaist] = useState(measurements.waist ?? "");
  const [hip, setHip] = useState(measurements.hip ?? "");
  const [neck, setNeck] = useState(measurements.neck ?? "");

  // Health
  const [diagnosis, setDiagnosis] = useState(typeof health.currentConditions === "string" ? health.currentConditions : "");
  const [symptoms, setSymptoms] = useState(symptomsInit);
  // iter326 — Allergies / Medications / Family history
  const allergiesInit = Array.isArray(health.allergies) ? health.allergies.join(", ")
    : (typeof health.allergies === "string" ? health.allergies : "");
  const medsInit = Array.isArray(health.medications) ? health.medications.join(", ")
    : (typeof health.medications === "string" ? health.medications : "");
  const [allergies, setAllergies] = useState(allergiesInit);
  const [medications, setMedications] = useState(medsInit);
  const [familyHistory, setFamilyHistory] = useState(
    typeof health.familyHistory === "string" ? health.familyHistory : ""
  );

  // Lifestyle
  const [activityLevel, setActivityLevel] = useState(lifestyle.activityLevel ?? "moderate");
  const [primaryGoal, setPrimaryGoal] = useState(lifestyle.primaryGoal ?? "general_health");
  const [sleepHours, setSleepHours] = useState(lifestyle.sleepHours ?? "");
  const [stressLevel, setStressLevel] = useState(lifestyle.stressLevel ?? "");

  // Labs (5 most common)
  const [glucose, setGlucose] = useState(labs.fastingGlucose ?? "");
  const [insulin, setInsulin] = useState(labs.fastingInsulin ?? "");
  const [hba1c, setHba1c] = useState(labs.hba1c ?? "");
  const [tsh, setTsh] = useState(labs.tsh ?? "");
  const [vitD, setVitD] = useState(labs.vitaminD ?? "");

  const [visitNote, setVisitNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("identity");

  // iter326 — Reinitialize fields when patient data changes. Use the full
  // health/personal stringified snapshot to detect any backend-side change
  // after fetchDetail (previously relied on patient.updated_at which the
  // doctor endpoint does not always set, leaving values stale).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setName(patient?.name ?? "");
    setEmail(patient?.email ?? "");
    setAge(personal.age ?? "");
    setGender(personal.gender ?? "");
    setWeight(body.weight ?? "");
    setHeight(body.height ?? "");
    setWaist(measurements.waist ?? "");
    setHip(measurements.hip ?? "");
    setNeck(measurements.neck ?? "");
    setDiagnosis(typeof health.currentConditions === "string" ? health.currentConditions : "");
    setSymptoms(symptomsInit);
    setAllergies(allergiesInit);
    setMedications(medsInit);
    setFamilyHistory(typeof health.familyHistory === "string" ? health.familyHistory : "");
    setActivityLevel(lifestyle.activityLevel ?? "moderate");
    setPrimaryGoal(lifestyle.primaryGoal ?? "general_health");
    setSleepHours(lifestyle.sleepHours ?? "");
    setStressLevel(lifestyle.stressLevel ?? "");
    setGlucose(labs.fastingGlucose ?? "");
    setInsulin(labs.fastingInsulin ?? "");
    setHba1c(labs.hba1c ?? "");
    setTsh(labs.tsh ?? "");
    setVitD(labs.vitaminD ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, JSON.stringify({ p: patient?.profile, n: patient?.name, e: patient?.email })]);

  const handleSave = async () => {
    setSaving(true);
    const errors = [];
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // 1. Quick input — body, diagnosis, symptoms, visit note, allergies, meds, family history
      try {
        await axios.post(`${API}/doctor/patients/${patientId}/quick-input`, {
          weight: weight !== "" ? parseFloat(weight) : null,
          height: height !== "" ? parseFloat(height) : null,
          diagnosis: diagnosis || null,
          symptoms: symptoms ? symptoms.split(",").map(s => s.trim()).filter(Boolean) : null,
          notes: visitNote || null,
          allergies: allergies !== allergiesInit
            ? allergies.split(",").map(s => s.trim()).filter(Boolean)
            : null,
          medications: medications !== medsInit
            ? medications.split(",").map(s => s.trim()).filter(Boolean)
            : null,
          family_history: familyHistory || null,
        }, { headers });
      } catch (e) {
        errors.push(`quick-input: ${e?.response?.data?.detail || e.message || "failed"}`);
      }

      // 2. Identity (name/email/age/gender)
      const idChanged = (
        (name && name !== patient?.name) ||
        (age !== "" && parseInt(age) !== personal.age) ||
        (gender && gender !== personal.gender)
      );
      if (idChanged) {
        try {
          await axios.patch(`${API}/doctor/patients/${patientId}/profile`, {
            name: name || null,
            age: age !== "" ? parseInt(age) : null,
            gender: gender || null,
          }, { headers });
        } catch (e) {
          errors.push(`identity: ${e?.response?.data?.detail || e.message || "failed"}`);
        }
      }

      // 3. Extras — measurements, lifestyle, labs, email
      try {
        await axios.put(`${API}/doctor/patients/${patientId}/profile-extras`, {
          email: email || null,
          measurements: {
            waist: waist !== "" ? parseFloat(waist) : null,
            hip: hip !== "" ? parseFloat(hip) : null,
            neck: neck !== "" ? parseFloat(neck) : null,
          },
          lifestyle: {
            activityLevel: activityLevel || null,
            primaryGoal: primaryGoal || null,
            sleepHours: sleepHours !== "" ? parseFloat(sleepHours) : null,
            stressLevel: stressLevel || null,
          },
          labs: {
            fastingGlucose: glucose !== "" ? parseFloat(glucose) : null,
            fastingInsulin: insulin !== "" ? parseFloat(insulin) : null,
            hba1c: hba1c !== "" ? parseFloat(hba1c) : null,
            tsh: tsh !== "" ? parseFloat(tsh) : null,
            vitaminD: vitD !== "" ? parseFloat(vitD) : null,
          },
        }, { headers });
      } catch (e) {
        errors.push(`extras: ${e?.response?.data?.detail || e.message || "failed"}`);
      }

      if (errors.length === 0) {
        toast.success(bg ? "Профилът е обновен" : "Profile updated");
        setVisitNote("");
      } else {
        toast.error((bg ? "Частичен запис. Неуспешни: " : "Partial save. Failed: ") + errors.join(" · "));
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при запис" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-3 py-2 text-xs rounded-lg border outline-none
    ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white placeholder:text-gray-500"
             : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"}`;
  const labelCls = "text-[10px] uppercase tracking-wider font-semibold text-gray-500";

  const SECTIONS = [
    { key: "identity",     bg: "Лични",         en: "Identity" },
    { key: "body",         bg: "Тяло",          en: "Body" },
    { key: "measurements", bg: "Измервания",    en: "Measurements" },
    { key: "labs",         bg: "Лаборатории",   en: "Labs" },
    { key: "lifestyle",    bg: "Начин на живот", en: "Lifestyle" },
    { key: "health",       bg: "Здраве",        en: "Health" },
    { key: "allergies",    bg: "Алергии",       en: "Allergies" },
    { key: "meds",         bg: "Медикаменти",   en: "Medications" },
    { key: "calculators",  bg: "Калкулатори",   en: "Calculators" },
  ];

  return (
    <div className={cardCls} data-testid="patient-edit-form">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-sm font-bold"
        data-testid="patient-edit-toggle"
      >
        <span className="flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-violet-500" />
          {bg ? "Редактирай пациентско досие" : "Edit patient profile"}
        </span>
        <span className="text-xs text-gray-500">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {/* Section tabs */}
          <div className="flex flex-wrap gap-1">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors
                  ${section === s.key
                    ? "bg-violet-600 text-white"
                    : (isDark ? "bg-white/5 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:text-gray-800")}`}
                data-testid={`edit-section-${s.key}`}
              >
                {bg ? s.bg : s.en}
              </button>
            ))}
          </div>

          {section === "identity" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className={labelCls + " sm:col-span-2"}>
                {bg ? "Пълно име" : "Full name"}
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-name-input" />
              </label>
              <label className={labelCls + " sm:col-span-2"}>
                {bg ? "Имейл" : "Email"}
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-email-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Възраст" : "Age"}
                <input type="number" value={age} onChange={(e) => setAge(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-age-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Пол" : "Gender"}
                <select value={gender} onChange={(e) => setGender(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-gender-input">
                  <option value="">—</option>
                  <option value="male">{bg ? "Мъж" : "Male"}</option>
                  <option value="female">{bg ? "Жена" : "Female"}</option>
                  <option value="other">{bg ? "Друг" : "Other"}</option>
                </select>
              </label>
            </div>
          )}

          {section === "body" && (
            <div className="grid grid-cols-2 gap-2">
              <label className={labelCls}>
                {bg ? "Тегло (kg)" : "Weight (kg)"}
                <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-weight-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Височина (cm)" : "Height (cm)"}
                <input type="number" step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-height-input" />
              </label>
            </div>
          )}

          {section === "measurements" && (
            <div className="grid grid-cols-3 gap-2">
              <label className={labelCls}>
                {bg ? "Талия (cm)" : "Waist (cm)"}
                <input type="number" step="0.1" value={waist} onChange={(e) => setWaist(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-waist-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Ханш (cm)" : "Hip (cm)"}
                <input type="number" step="0.1" value={hip} onChange={(e) => setHip(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-hip-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Шия (cm)" : "Neck (cm)"}
                <input type="number" step="0.1" value={neck} onChange={(e) => setNeck(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-neck-input" />
              </label>
            </div>
          )}

          {section === "labs" && (
            <div className="grid grid-cols-2 gap-2">
              <label className={labelCls}>
                {bg ? "Глюкоза (мг/дл)" : "Glucose (mg/dL)"}
                <input type="number" step="0.1" value={glucose} onChange={(e) => setGlucose(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-glucose-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Инсулин (μU/mL)" : "Insulin (μU/mL)"}
                <input type="number" step="0.1" value={insulin} onChange={(e) => setInsulin(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-insulin-input" />
              </label>
              <label className={labelCls}>HbA1c (%)
                <input type="number" step="0.1" value={hba1c} onChange={(e) => setHba1c(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-hba1c-input" />
              </label>
              <label className={labelCls}>TSH
                <input type="number" step="0.01" value={tsh} onChange={(e) => setTsh(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-tsh-input" />
              </label>
              <label className={labelCls + " col-span-2"}>
                {bg ? "Витамин D (ng/mL)" : "Vitamin D (ng/mL)"}
                <input type="number" step="0.1" value={vitD} onChange={(e) => setVitD(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-vitd-input" />
              </label>
            </div>
          )}

          {section === "lifestyle" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className={labelCls}>
                {bg ? "Активност" : "Activity level"}
                <select value={activityLevel} onChange={(e) => setActivityLevel(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-activity-input">
                  <option value="sedentary">{bg ? "Седнал" : "Sedentary"}</option>
                  <option value="light">{bg ? "Лека" : "Light"}</option>
                  <option value="moderate">{bg ? "Умерена" : "Moderate"}</option>
                  <option value="active">{bg ? "Активна" : "Active"}</option>
                  <option value="very_active">{bg ? "Много активна" : "Very active"}</option>
                </select>
              </label>
              <label className={labelCls}>
                {bg ? "Цел" : "Primary goal"}
                <select value={primaryGoal} onChange={(e) => setPrimaryGoal(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-goal-input">
                  <option value="general_health">{bg ? "Общо здраве" : "General health"}</option>
                  <option value="weight_loss">{bg ? "Отслабване" : "Weight loss"}</option>
                  <option value="muscle_gain">{bg ? "Мускулна маса" : "Muscle gain"}</option>
                  <option value="metabolic_reset">{bg ? "Метаболитно нулиране" : "Metabolic reset"}</option>
                  <option value="condition_management">{bg ? "Управление на състояние" : "Condition management"}</option>
                </select>
              </label>
              <label className={labelCls}>
                {bg ? "Сън (часа)" : "Sleep (hrs)"}
                <input type="number" step="0.5" value={sleepHours} onChange={(e) => setSleepHours(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-sleep-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Стрес" : "Stress"}
                <select value={stressLevel} onChange={(e) => setStressLevel(e.target.value)} className={inputCls + " mt-1"} data-testid="edit-stress-input">
                  <option value="">—</option>
                  <option value="low">{bg ? "Нисък" : "Low"}</option>
                  <option value="moderate">{bg ? "Умерен" : "Moderate"}</option>
                  <option value="high">{bg ? "Висок" : "High"}</option>
                </select>
              </label>
            </div>
          )}

          {section === "health" && (
            <div className="grid grid-cols-1 gap-2">
              <label className={labelCls}>
                {bg ? "Диагноза / състояния" : "Diagnosis / conditions"}
                <input type="text" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} className={inputCls + " mt-1"} placeholder={bg ? "напр. диабет тип 2, хипертония, рахит" : "e.g. type 2 diabetes, hypertension, rickets"} data-testid="edit-diagnosis-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Симптоми (разделени със запетая)" : "Symptoms (comma separated)"}
                <input type="text" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} className={inputCls + " mt-1"} placeholder={bg ? "напр. умора, главоболие" : "e.g. fatigue, headache"} data-testid="edit-symptoms-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Семейна анамнеза" : "Family history"}
                <input type="text" value={familyHistory} onChange={(e) => setFamilyHistory(e.target.value)} className={inputCls + " mt-1"} placeholder={bg ? "напр. диабет (баща), рак на гърдата (леля)" : "e.g. diabetes (father), breast cancer (aunt)"} data-testid="edit-family-history-input" />
              </label>
              <label className={labelCls}>
                {bg ? "Бележка от прегледа" : "Visit note"}
                <textarea rows={2} value={visitNote} onChange={(e) => setVisitNote(e.target.value)} className={inputCls + " mt-1 resize-none"} data-testid="edit-notes-input" />
              </label>
            </div>
          )}

          {section === "allergies" && (
            <div className="grid grid-cols-1 gap-2">
              <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? "Изброй алергиите и непоносимостите на пациента, разделени със запетая. Тези данни се използват от Meal Intelligence AI и AI препоръките за добавки/лекарства."
                  : "List the patient's allergies/intolerances separated by commas. Used by Meal Intelligence AI and supplement/drug recommendations."}
              </p>
              <label className={labelCls}>
                {bg ? "Алергии и непоносимости" : "Allergies & intolerances"}
                <textarea
                  rows={3}
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  className={inputCls + " mt-1 resize-none"}
                  placeholder={bg ? "напр. фъстъци, лактоза, глутен, морски дарове, пеницилин" : "e.g. peanuts, lactose, gluten, shellfish, penicillin"}
                  data-testid="edit-allergies-input"
                />
              </label>
            </div>
          )}

          {section === "meds" && (
            <div className="grid grid-cols-1 gap-2">
              <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? "Активни медикаменти и добавки, разделени със запетая. Използват се от Digital Twin (drug-food interactions) и AI Pharma съветника."
                  : "Active medications & supplements, comma separated. Used by Digital Twin (drug-food interactions) and AI Pharma advisor."}
              </p>
              <label className={labelCls}>
                {bg ? "Медикаменти и добавки" : "Medications & supplements"}
                <textarea
                  rows={4}
                  value={medications}
                  onChange={(e) => setMedications(e.target.value)}
                  className={inputCls + " mt-1 resize-none"}
                  placeholder={bg ? "напр. Metformin 500mg 2x/d, Levothyroxine 50µg, Omega-3 1g" : "e.g. Metformin 500mg 2x/d, Levothyroxine 50µg, Omega-3 1g"}
                  data-testid="edit-medications-input"
                />
              </label>
            </div>
          )}

          {section === "calculators" && (
            <CalculatorsSection
              bg={bg}
              isDark={isDark}
              weight={weight}
              height={height}
              waist={waist}
              hip={hip}
              neck={neck}
              age={age}
              gender={gender}
              glucose={glucose}
              insulin={insulin}
              hba1c={hba1c}
            />
          )}

          <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-white/5">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold hover:opacity-95 disabled:opacity-60"
              data-testid="patient-edit-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {saving ? (bg ? "Запазвам..." : "Saving...") : (bg ? "Запази" : "Save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * PatientLabAnalysisList — списък на качените лаборатории + бутон „Анализирай"
 * за всеки запис. Извиква POST /doctor/patients/{id}/labs/{lab_id}/analyze
 * което парсва PDF/изображение и записва lab values в profile. (#1)
 */
function PatientLabAnalysisList({ patientId, API, isDark, bg, cardCls, onAnalyzed }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [resultById, setResultById] = useState({});

  const fetchLabs = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.get(`${API}/doctor/patients/${patientId}/labs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setLabs(data.labs || []);
    } catch { setLabs([]); }
    finally { setLoading(false); }
  }, [API, patientId]);

  useEffect(() => { fetchLabs(); }, [fetchLabs]);

  // Re-fetch when an upload happens elsewhere
  useEffect(() => {
    const h = () => fetchLabs();
    window.addEventListener("doctor-lab-uploaded", h);
    return () => window.removeEventListener("doctor-lab-uploaded", h);
  }, [fetchLabs]);

  const handleAnalyze = async (labId) => {
    setAnalyzingId(labId);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients/${patientId}/labs/${labId}/analyze`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResultById(prev => ({ ...prev, [labId]: data }));
      const nFind = (data?.findings || []).length;
      const nRec  = (data?.recommendations || []).length;
      const nHidden = (data?.hidden_patterns || []).length;
      const summary = bg
        ? `Анализът е готов · ${nFind} открития · ${nRec} препоръки${nHidden > 0 ? ` · ${nHidden} скрити модела` : ""}`
        : `Analysis ready · ${nFind} findings · ${nRec} recs${nHidden > 0 ? ` · ${nHidden} hidden patterns` : ""}`;
      toast.success(summary);
      onAnalyzed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при анализа" : "Analysis error"));
    } finally {
      setAnalyzingId(null);
    }
  };

  // iter326 — Преглед на оригиналния качен файл (PDF/изображение).
  const handleView = async (labId, filename) => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/doctor/patients/${patientId}/labs/${labId}/file`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked → fallback download
        const a = document.createElement("a");
        a.href = url; a.download = filename || `lab-${labId}`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
      }
      // Revoke след 60 сек, за да даде време на новия таб да зареди
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при отваряне" : "Open failed"));
    }
  };

  if (loading) return null;
  if (labs.length === 0) return null;

  return (
    <div className={cardCls} data-testid="patient-lab-analysis-list">
      <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4 text-violet-500" />
        {bg ? "AI анализ на качените лаборатории" : "AI analysis of uploaded labs"}
      </h3>
      <ul className="space-y-2">
        {labs.map((l) => {
          const result = resultById[l.id];
          const busy = analyzingId === l.id;
          return (
            <li
              key={l.id}
              className={`p-2.5 rounded-lg text-xs ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}
              data-testid={`lab-analyze-row-${l.id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{l.filename}</p>
                  <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {new Date(l.created_at).toLocaleString(bg ? "bg-BG" : "en-US")}
                  </p>
                </div>
                <button
                  onClick={() => handleView(l.id, l.filename)}
                  className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-white/5 hover:bg-white/10 dark:text-gray-200 text-gray-700 border border-gray-300/30 text-[11px] font-semibold"
                  data-testid={`view-lab-btn-${l.id}`}
                >
                  <Eye className="w-3 h-3" />
                  {bg ? "Виж" : "View"}
                </button>
                <button
                  onClick={() => handleAnalyze(l.id)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[11px] font-semibold hover:opacity-95 disabled:opacity-60"
                  data-testid={`analyze-lab-btn-${l.id}`}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {busy ? (bg ? "Анализирам..." : "Analyzing...") : (bg ? "Анализирай" : "Analyze")}
                </button>
              </div>
              {result && (
                <div className={`mt-2 pt-2 border-t text-[11px] leading-relaxed space-y-2.5
                  ${isDark ? "border-white/5 text-gray-300" : "border-gray-200 text-gray-700"}`}>
                  {result.summary && <p><strong>{bg ? "Обобщение:" : "Summary:"}</strong> {result.summary}</p>}

                  {/* AI Findings */}
                  {Array.isArray(result.findings) && result.findings.length > 0 && (
                    <div data-testid={`lab-findings-${l.id}`}>
                      <p className="font-semibold text-[10px] uppercase tracking-wider text-violet-500 mb-1">
                        {bg ? "🩺 AI Открития" : "🩺 AI Findings"}
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {result.findings.map((f, i) => <li key={i}>{typeof f === "string" ? f : f.text || JSON.stringify(f)}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* AI Recommendations */}
                  {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
                    <div data-testid={`lab-recommendations-${l.id}`}>
                      <p className="font-semibold text-[10px] uppercase tracking-wider text-emerald-500 mb-1">
                        {bg ? "💡 Препоръки" : "💡 Recommendations"}
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {result.recommendations.map((r, i) => <li key={i}>{typeof r === "string" ? r : r.text || JSON.stringify(r)}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* iter398 — Hidden multi-bot patterns */}
                  {Array.isArray(result.hidden_patterns) && result.hidden_patterns.length > 0 && (
                    <div data-testid={`lab-hidden-patterns-${l.id}`} className="space-y-1.5">
                      <p className="font-semibold text-[10px] uppercase tracking-wider text-amber-500 mb-1">
                        {bg ? "🔍 Скрити модели (Endo/Nephro/Hemo Bot)" : "🔍 Hidden patterns (Endo/Nephro/Hemo Bot)"}
                      </p>
                      {result.hidden_patterns.map((p) => (
                        <div
                          key={p.id}
                          data-testid={`hidden-pattern-${p.id}`}
                          className={`p-2 rounded-md border-l-2 ${
                            p.severity === "high" ? "border-rose-500 bg-rose-500/5" :
                            p.severity === "moderate" ? "border-amber-500 bg-amber-500/5" :
                            "border-sky-500 bg-sky-500/5"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <strong className={`text-[11px] ${isDark ? "text-gray-100" : "text-gray-900"}`}>{bg ? p.title_bg : p.title_en}</strong>
                            <div className="flex gap-1">
                              {(p.bots || []).map((b) => (
                                <span key={b} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300">{b}</span>
                              ))}
                            </div>
                          </div>
                          <p className="text-[10px] italic mb-1 opacity-90">{bg ? p.evidence_bg : p.evidence_en}</p>
                          {Array.isArray(p.actions_bg || p.actions_en) && (
                            <ul className="list-disc pl-4 text-[10px] space-y-0.5">
                              {(bg ? p.actions_bg : p.actions_en).map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {result.parsed_values && Object.keys(result.parsed_values).length > 0 && (
                    <details className="text-[10px] cursor-pointer">
                      <summary className="opacity-70">{bg ? "Парснати стойности" : "Parsed values"}</summary>
                      <p className="opacity-90 mt-1">{Object.entries(result.parsed_values).map(([k, v]) => `${k}=${v}`).join(", ")}</p>
                    </details>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


/**
 * CalculatorsSection — read-only клинични калкулатори, изчислени live от
 * текущите стойности във формата (BMI, BSA, ИТМ, BAI, WHR, NHR, HOMA-IR,
 * eAG, FFMI). Не записват в DB, помагат на лекаря да види моментно
 * разчитане докато редактира.
 */
function CalculatorsSection({ bg, isDark, weight, height, waist, hip, neck, age, gender, glucose, insulin, hba1c }) {
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const w = num(weight);    // kg
  const h = num(height);    // cm
  const wa = num(waist);    // cm
  const hp = num(hip);      // cm
  const ne = num(neck);     // cm
  const a = num(age);
  const g = num(glucose);   // mmol/L (BG default)
  const ins = num(insulin); // µU/mL
  const a1c = num(hba1c);   // %

  const bmi = w && h ? w / Math.pow(h / 100, 2) : null;
  const bsa = w && h ? Math.sqrt((w * h) / 3600) : null; // Mosteller
  const whr = wa && hp ? wa / hp : null;
  const bai = hp && h ? (hp / Math.pow(h / 100, 1.5)) - 18 : null;
  // HOMA-IR (mmol/L * µU/mL / 22.5)
  const homaIr = g && ins ? (g * ins) / 22.5 : null;
  // QUICKI = 1 / (log(insulin) + log(glucose mg/dL))
  const glucoseMgDl = g ? g * 18.0182 : null;
  const quicki = ins && glucoseMgDl ? 1 / (Math.log10(ins) + Math.log10(glucoseMgDl)) : null;
  // eAG (estimated avg glucose) from HbA1c → mmol/L
  const eag = a1c ? (1.59 * a1c - 2.59) : null;
  // Body fat % via US Navy formula (cm)
  let bfPct = null;
  if (h && wa && ne) {
    if (gender === "male") {
      bfPct = 86.010 * Math.log10(wa - ne) - 70.041 * Math.log10(h) + 36.76;
    } else if (gender === "female" && hp) {
      bfPct = 163.205 * Math.log10(wa + hp - ne) - 97.684 * Math.log10(h) - 78.387;
    }
  }
  // Mifflin-St Jeor BMR
  let bmr = null;
  if (w && h && a) {
    if (gender === "male") bmr = 10 * w + 6.25 * h - 5 * a + 5;
    else if (gender === "female") bmr = 10 * w + 6.25 * h - 5 * a - 161;
  }

  const fmt = (v, digits = 1) => (v == null ? "—" : v.toFixed(digits));

  const bmiCls = (() => {
    if (!bmi) return "text-gray-500";
    if (bmi < 18.5) return "text-amber-500";
    if (bmi < 25) return "text-emerald-500";
    if (bmi < 30) return "text-amber-500";
    return "text-rose-500";
  })();
  const bmiLabel = (() => {
    if (!bmi) return "—";
    if (bmi < 18.5) return bg ? "Поднормено" : "Underweight";
    if (bmi < 25) return bg ? "Норма" : "Normal";
    if (bmi < 30) return bg ? "Наднормено" : "Overweight";
    if (bmi < 35) return bg ? "Затлъстяване I" : "Obese I";
    if (bmi < 40) return bg ? "Затлъстяване II" : "Obese II";
    return bg ? "Затлъстяване III" : "Obese III";
  })();

  const items = [
    { key: "bmi",    label: "BMI",                  value: fmt(bmi),     unit: "kg/m²", note: bmiLabel, cls: bmiCls },
    { key: "bsa",    label: "BSA (Mosteller)",      value: fmt(bsa, 2),  unit: "m²" },
    { key: "whr",    label: bg ? "WHR (талия/ханш)" : "Waist-Hip Ratio", value: fmt(whr, 2), unit: "" },
    { key: "bai",    label: "BAI",                  value: fmt(bai),     unit: "%" },
    { key: "bfPct",  label: bg ? "Телесни мазнини (Navy)" : "Body Fat % (Navy)", value: fmt(bfPct), unit: "%" },
    { key: "bmr",    label: "BMR (Mifflin-St Jeor)", value: fmt(bmr, 0), unit: "kcal/d" },
    { key: "homaIr", label: "HOMA-IR",              value: fmt(homaIr, 2), unit: "", note: homaIr ? (homaIr < 1.5 ? (bg ? "Норма" : "Normal") : homaIr < 2.5 ? (bg ? "Гранична" : "Borderline") : (bg ? "Резистентност" : "Resistant")) : "" },
    { key: "quicki", label: "QUICKI",               value: fmt(quicki, 3), unit: "" },
    { key: "eag",    label: bg ? "eAG (от HbA1c)" : "eAG (from HbA1c)", value: fmt(eag, 1), unit: "mmol/L" },
  ];

  return (
    <div className="space-y-2" data-testid="calculators-section">
      <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        {bg
          ? "Изчислява се live от въведените тегло/ръст/обиколки/глюкоза/инсулин. Промените във формата се отразяват веднага. Не се запазват — стойностите идват от съответните полета."
          : "Computed live from entered weight/height/circumferences/glucose/insulin. Changes reflect instantly. Not stored — values come from form fields."}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it) => (
          <div
            key={it.key}
            className={`p-2.5 rounded-lg border ${isDark ? "bg-[hsl(222,47%,8%)] border-white/5" : "bg-gray-50 border-gray-200"}`}
            data-testid={`calc-${it.key}`}
          >
            <div className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {it.label}
            </div>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className={`text-base font-bold ${it.cls || (isDark ? "text-white" : "text-gray-900")}`}>{it.value}</span>
              {it.unit && <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>{it.unit}</span>}
            </div>
            {it.note && <div className={`text-[10px] mt-0.5 ${it.cls || (isDark ? "text-gray-400" : "text-gray-500")}`}>{it.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
